import i18next from 'i18next'
import {
  createUser,
  editUser,
  createHousehold,
  unassignHousehold,
  assingHousehold,
} from './createUserApi'
import {
  assignUser,
  assingTags,
  unassignTag,
  unassignFamilyRelation,
} from './usersApi'
import { snackBar } from 'store'

// The family-relation and tag rows that a save has to clear before it
// re-creates them, carried together with the contact they were read from.
// `contactId` is what keeps this safe: relations are addressed by relation id
// alone, with nothing in the id tying them to a contact, so a set read from one
// contact would happily delete that contact's relations while a different one
// was being saved. Everything here is ignored unless it belongs to the contact
// actually being written.
export type SaveScratch = {
  contactId: number
  familyRelationIds: number[]
  tagRelations: any[]
}

export const emptySaveScratch = (): SaveScratch => ({
  contactId: 0,
  familyRelationIds: [],
  tagRelations: [],
})

export const saveFullUser = async (
  form: any,
  communityId: number,
  options?: {
    originalHousehold?: any
    scratch?: SaveScratch
  }
// Returns true when the whole save went through, null when it could not even
// start, or the failing response itself so the caller can report its status and
// code - the declared number was never what came back.
): Promise<any> => {
  const { originalHousehold, scratch } = options ?? {}

  // Create or update user
  let userId: number
  try {
    if (form.id && form.id !== 0) {
      const res = await editUser(form, communityId)
      if (res?.data?.status === 400) {
        return res
      }
      
      if (res?.data?.status === 409 && res?.data?.data?.code === 'DUPLICATE_USER') {
        return res
      }

      // Any other answer carrying a status is an error the caller can describe;
      // returning null here threw the status and its code away and left the
      // import log saying only "error".
      if (res?.data?.status && res.data.status >= 400) {
        return res
      }

      if (!res.data?.user) {
        return null
      }

      userId = res.data.user.userId
    } else {
      const res = await createUser(form, communityId)
      if (res?.data?.status === 400) {
        return res
      }
      
      if (res?.data?.status === 409 && res?.data?.data?.code === 'DUPLICATE_USER') {
        return res
      }

      if (res?.data?.status && res.data.status >= 400) {
        return res
      }

      if (!res.data?.user) {
        return null
      }

      userId = res.data.user.userId
    }
 
    // Household
    if (form.family?.household?.firstName) {
      if (!form.family.household.id) {
        await createHousehold(userId, form.family.household, communityId)
      } else if (form.family.household.firstName !== originalHousehold?.firstName) {
        await unassignHousehold(userId, communityId)
        await createHousehold(userId, form.family.household, communityId)
      }
    }

    // Only rows read from the contact we just wrote may be cleared. A set left
    // over from another contact is dropped here instead of deleting that
    // contact's relations - which is what used to happen, since relation ids
    // carry no owner and nothing checked.
    const clearsThisContact = !!scratch && scratch.contactId !== 0 && scratch.contactId === userId
    const oldFamilyRelationIds = clearsThisContact ? scratch!.familyRelationIds : []
    const oldTagRelation = clearsThisContact ? scratch!.tagRelations : []

    // Unassign old family relations
    for (const relationId of oldFamilyRelationIds) {
      await unassignFamilyRelation({ relationId, communityId })
    }

    // Children
    for (const child of form.family?.children ?? []) {
      if (child.mode === 'select') {
        if (child.id && child.id !== 0) {
          await assignUser({ userId, communityId, relatedUserId: child.id, relationTypeId: 2 })
        }
      } else if (child.mode === 'new' && child.firstName?.trim()) {
        const res = await createUser({ population: child }, communityId)
        if (res?.data?.user?.userId) {
          await assignUser({ userId, communityId, relatedUserId: res.data.user.userId, relationTypeId: 2 })
        } else {
          snackBar.showError(i18next.t('population:child_create_failed'))
        }
      }
    }

    // Spouse
    if (form.family?.spouse) {
      const spouse = form.family.spouse
      if (spouse.mode === 'select') {
        if (spouse.id && spouse.id > 0) {
          await assignUser({ userId, communityId, relatedUserId: spouse.id, relationTypeId: 3 })
        }
      } else if (spouse.mode === 'new' && spouse.firstName?.trim()) {
        const res = await createUser({ population: spouse }, communityId)
        if (res?.data?.user?.userId) {
          await assignUser({ userId, communityId, relatedUserId: res.data.user.userId, relationTypeId: 3 })
        } else {
          snackBar.showError(i18next.t('population:spouse_create_failed'))
        }
      }
    }

    // Unassign old tags
    for (const tag of oldTagRelation) {
      await unassignTag({ userId, communityId, tag: tag.tag, tagTypeId: tag.tagTypeId })
    }

    // Save tags
    for (const userType of form.population?.userTypes ?? []) {
      await assingTags({ userId, communityId, tag: userType.tagName, tagTypeId: userType.tagTypeId })
    }
    
    for (const role of form.contacts?.roles ?? []) {
      await assingTags({ userId, communityId, tag: role.tagName, tagTypeId: role.tagTypeId })
    }
    
    for (const tag of form.contacts?.tags ?? []) {
      await assingTags({ userId, communityId, tag: tag.tagName, tagTypeId: tag.tagTypeId })
    }

    return true
  } catch (e) {
    throw e
  }

  return userId
}
